import { h } from 'preact';

import { SCENE_CSS } from './keyframes';
import {
    layerTransform, DEPTH_SKY, DEPTH_FAR, DEPTH_MID, DEPTH_GROUND, DEPTH_HERO,
} from './parallax';
import { Cloud, Tree, GroundBand } from './sceneProps';

// One paw print on the ground band.
const Paw = ({ x, y = 140, fill }) => (
    <g fill={fill} opacity='0.45'>
        <ellipse cx={x} cy={y} rx='5' ry='3.5' />
        <circle cx={x - 5} cy={y - 6} r='1.5' />
        <circle cx={x - 2} cy={y - 8} r='1.5' />
        <circle cx={x + 2} cy={y - 8} r='1.5' />
        <circle cx={x + 5} cy={y - 6} r='1.5' />
    </g>
);

const VetDog = ({
    progress = 0, refreshing = false, pastThreshold = false, released = false,
    pullOffset = 0, palette = {},
}) => {
    const driftSpeed = (30 - 16 * progress).toFixed(2);
    let dogAnimation = '';
    if (released) {
        dogAnimation = 'animation: refreshDogShake 0.6s ease-in-out;';
    } else if (refreshing) {
        dogAnimation = 'animation: refreshHeroBob 0.5s ease-in-out infinite;';
    }

    return (
        <div style={`position: relative; width: 100%; height: 100%; ${pastThreshold ? 'filter: brightness(1.1);' : ''}`}>
            <svg
                viewBox='0 0 800 150'
                preserveAspectRatio='xMidYMax slice'
                role='img'
                class='refresh-scene'
                style='position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible;'
            >
                <style>{SCENE_CSS}</style>
                <title>Dra ned for å oppdatere</title>

                {/* Sky */}
                <g style={layerTransform(pullOffset, DEPTH_SKY)}>
                    <Cloud x={110} y={24} fill={palette.sky} />
                    <Cloud x={370} y={16} s={0.8} fill={palette.sky} />
                    <Cloud x={600} y={28} s={1.1} fill={palette.sky} />
                </g>

                {/* Far — rolling hills */}
                <g style={layerTransform(pullOffset, DEPTH_FAR)}>
                    <path d='M-100 150 L-100 102 Q 120 78 320 96 T 640 92 L 900 100 L 900 150 Z' fill={palette.far} />
                </g>

                {/* Mid — fence, trees and the clinic house, drifting by */}
                <g style={layerTransform(pullOffset, DEPTH_MID)}>
                    <g class='drift' style={`animation-duration: ${driftSpeed}s;`}>
                        {[0, 800].map((off) => (
                            <g key={off} transform={`translate(${off} 0)`}>
                                {[60, 84, 108, 132, 156, 180, 204, 228].map((x) => (
                                    <rect key={x} x={x} y='98' width='4' height='24' fill={palette.mid} />
                                ))}
                                <rect x='58' y='102' width='174' height='3' fill={palette.mid} />
                                <rect x='58' y='111' width='174' height='3' fill={palette.mid} />
                                <Tree x={320} fill={palette.mid} />
                                <Tree x={470} y={82} s={0.7} fill={palette.mid} />
                                {/* Clinic house with cross */}
                                <rect x='560' y='72' width='72' height='50' fill={palette.mid} />
                                <polygon points='552,72 596,46 640,72' fill={palette.mid} />
                                <rect x='594' y='52' width='4' height='14' fill={palette.sky} />
                                <rect x='589' y='57' width='14' height='4' fill={palette.sky} />
                                <rect x='588' y='98' width='16' height='24' fill={palette.backdrop} opacity='0.5' />
                                <Tree x={720} s={1.1} fill={palette.mid} />
                            </g>
                        ))}
                    </g>
                </g>

                {/* Ground — with drifting paw prints */}
                <g style={layerTransform(pullOffset, DEPTH_GROUND)}>
                    <GroundBand fill={palette.ground} />
                    <g class='drift' style={`animation-duration: ${(driftSpeed / 2).toFixed(2)}s;`}>
                        {[0, 800].map((off) => (
                            <g key={off} transform={`translate(${off} 0)`}>
                                {[100, 260, 420, 580, 740].map((x) => (
                                    <Paw key={x} x={x} fill={palette.text} />
                                ))}
                            </g>
                        ))}
                    </g>
                </g>

                {/* Release — heart pop above the dog */}
                {released && (
                    <g style='animation: refreshHeartPop 0.8s ease-out forwards; transform-origin: 409px 58px;'>
                        <path
                            d='M400 52 C400 46 406 44 409 48 C412 44 418 46 418 52 C418 58 409 64 409 64 C409 64 400 58 400 52 Z'
                            fill={palette.accent}
                        />
                    </g>
                )}
            </svg>

            {/* Hero — dog trotting on the ground */}
            <div style={`position: absolute; inset: 0; display: flex; align-items: flex-end; justify-content: center; pointer-events: none; ${layerTransform(pullOffset, DEPTH_HERO)}`}>
                <i
                    class='fa-duotone fa-dog'
                    style={`font-size: 72px; color: ${palette.text}; --fa-secondary-opacity: 0.5; padding-bottom: 26px; ${dogAnimation}`}
                />
            </div>
        </div>
    );
};

export default VetDog;
